import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Scale, Utensils, Dumbbell, MessagesSquare, Brain, Gamepad2,
  TrendingUp, TrendingDown, Minus, Wallet, FileText, Layers,
  Stethoscope, Compass, BookOpen, Plane, Languages, ChevronRight,
  Heart, Flame, VenetianMask, Check, Camera, Library,
} from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { useAuth } from '../../contexts/AuthContext'
import { useProfile } from '../../hooks/useProfile'
import { useWeightEntries } from '../../hooks/useWeightEntries'
import { useMealEntries } from '../../hooks/useMealEntries'
import { useSessions } from '../../hooks/useSessions'
import { useNewsArticles } from '../../hooks/useNews'
import { useMyAnswer, usePartnerAnswer } from '../../components/games/questions/hooks'
import { useJointStreak, usePartnerWordleAttempt } from '../../components/games/wordle/hooks'
import { useQuizRounds } from '../../components/games/quiz/hooks'
import { ROUND_SIZE } from '../../components/games/quiz/logic'
import { useSpyScoreboard } from '../../components/games/spy/hooks'
import { useHabits } from '../../hooks/useHabits'
import { useMomentsToday } from '../../hooks/useMomentsToday'
import { localDateKey as questionsLocalDateKey } from '../../components/games/questions/logic'
import { localDateKey as wordleLocalDateKey } from '../../components/games/wordle/gameLogic'
import { getPartnerId, getDisplayName, hasOwnerAccess, isOwner } from '../../lib/owner'
import NotificationBanner from '../../components/NotificationBanner'
import { useNotificationPrompt } from '../../hooks/useNotificationPrompt'
import TipCard from '../../components/TipCard'
import MoodCard from './components/MoodCard'
import QuickCapture from './components/QuickCapture'
import OnThisDayCard from './components/OnThisDayCard'
import DailyBriefingButton from './components/DailyBriefingButton'
import Avatar from '../../components/Avatar'
import { useTourAnchor } from '../../contexts/TourAnchorContext'
import { useUnits } from '../../contexts/UnitsContext'
import { cn } from '../../lib/utils'
import { supabase } from '../../lib/supabaseClient'

// "Today" home — landing surface that replaces the weight tab as the
// app's default first paint. Reads from existing hooks; nothing here
// fetches its own data the page below doesn't already need.
//
// Sections, top to bottom:
//   1. Greeting strip (name + date + joint streak chip)
//   2. Notification banner + tip card (carried over from the page
//      wrapper so they stay visible the moment the user opens the app
//      but only on Today — quiet on the other workspace tabs)
//   3. Today's check-ins (weight / meals / workout — pills with ✓
//      if logged, "+ log" CTA if not) + mood/energy tap card
//   4. Couples panel — daily question + Wordle state at a glance
//   5. Quick stats — current weight + delta vs prior entry
//   6. Top headline preview
//   7. Module grid — long-tail navigation
export default function TodayTab({ onSelectTab }) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { profile } = useProfile()
  const locale = i18n.language === 'ar' ? 'ar' : 'en'
  const partnerId = useMemo(() => getPartnerId(user), [user])
  const partnerName = useMemo(() => getDisplayName(partnerId, locale), [partnerId, locale])

  // Tour anchors — each refs a wrapper div the SpotlightTour reads
  // via useTourAnchorRegistry. Mounted here (not deeper) so the
  // controller can size the spotlight around the whole section.
  const greetingRef = useTourAnchor('today-greeting')
  const checkinsRef = useTourAnchor('today-checkins')
  const couplesRef = useTourAnchor('today-couples')
  const headlineRef = useTourAnchor('today-headline')
  const modulesRef = useTourAnchor('today-modules')

  return (
    <div className="space-y-4">
      <div ref={greetingRef}>
        <GreetingStrip
          t={t}
          locale={locale}
          name={profile?.name}
          myUserId={user?.id}
          partnerId={partnerId}
        />
      </div>
      <NotificationSlot />
      <TipCard />
      <div ref={checkinsRef}>
        <CheckInRow t={t} onSelectTab={onSelectTab} />
      </div>
      <MoodCard />
      <QuickCapture />
      <OnThisDayCard />
      <HabitsWidget t={t} navigate={navigate} />
      <div ref={couplesRef}>
        <CouplesPanel
          t={t}
          userId={user?.id}
          partnerId={partnerId}
          partnerName={partnerName}
          locale={locale}
        />
      </div>
      <QuickStats t={t} />
      <SpyScoreboardWidget t={t} navigate={navigate} />
      <div ref={headlineRef}>
        <TopHeadline t={t} locale={locale} />
      </div>
      <div ref={modulesRef}>
        <ModuleGrid t={t} user={user} navigate={navigate} />
      </div>
    </div>
  )
}

// ─── 1. Greeting ────────────────────────────────────────────────────
function GreetingStrip({ t, locale, name, myUserId, partnerId }) {
  const dateLabel = useMemo(() => {
    try {
      return new Date().toLocaleDateString(locale === 'ar' ? 'ar-EG' : 'en-US', {
        weekday: 'long', day: 'numeric', month: 'long',
      })
    } catch { return '' }
  }, [locale])

  // Joint streak from the daily-question shared streak — the most
  // representative "you and your partner are showing up together"
  // signal. Falls back silently if it's zero.
  const refreshKey = `${myUserId}|${partnerId}`
  const streak = useJointStreak(myUserId, partnerId, refreshKey)

  return (
    <div className="flex items-center gap-2 px-1" dir="auto">
      <div className="flex-1 min-w-0">
        <p className="text-base font-semibold text-foreground truncate" dir="auto">
          {name ? t('phoenixTracker.today.greetingNamed', { name }) : t('phoenixTracker.today.greetingPlain')}
        </p>
        <p className="text-xs text-muted-foreground truncate" dir="auto">{dateLabel}</p>
      </div>
      <DailyBriefingButton />
      {streak > 0 && (
        <span
          className="inline-flex items-center gap-1 text-xs font-bold text-app-primary-500 flex-shrink-0"
          dir="ltr"
        >
          <Heart className="w-3.5 h-3.5 fill-current" />
          <Flame className="w-3.5 h-3.5" />
          {streak}
        </span>
      )}
    </div>
  )
}

// ─── 2. Notification banner (mounts only when there's something to show) ─
function NotificationSlot() {
  const notif = useNotificationPrompt()
  if (!notif.bannerVisible) return null
  return (
    <NotificationBanner permission={notif.permission} onDismiss={notif.dismissBanner} />
  )
}

// ─── 3. Today's check-ins ───────────────────────────────────────────
function CheckInRow({ t, onSelectTab }) {
  const { entries: weights } = useWeightEntries()
  const { entries: meals } = useMealEntries()
  const { sessions } = useSessions()
  const navigate = useNavigate()

  const todayIso = useMemo(() => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }, [])

  const loggedWeightToday = weights.some((e) => e.date === todayIso)
  const loggedMealToday = meals.some((m) => (m.date || '').startsWith(todayIso))
  const loggedWorkoutToday = sessions.some((s) => (s.date || '').startsWith(todayIso))

  return (
    <div className="grid grid-cols-3 gap-2">
      <CheckInPill
        icon={Scale}
        label={t('phoenixTracker.today.weight')}
        done={loggedWeightToday}
        onClick={() => onSelectTab?.('weight')}
      />
      <CheckInPill
        icon={Utensils}
        label={t('phoenixTracker.today.meals')}
        done={loggedMealToday}
        onClick={() => onSelectTab?.('meals')}
      />
      <CheckInPill
        icon={Dumbbell}
        label={t('phoenixTracker.today.workout')}
        done={loggedWorkoutToday}
        onClick={() => navigate('/workout')}
      />
    </div>
  )
}

function CheckInPill({ icon: Icon, label, done, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-col items-center justify-center gap-1 px-2 py-3 rounded-lg border-2 transition-colors',
        done
          ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-500'
          : 'border-border bg-card hover:border-app-primary-500/40 hover:bg-app-primary-500/5',
      )}
      dir="auto"
    >
      <Icon className={cn('w-5 h-5', done ? 'text-emerald-500' : 'text-app-primary-500')} />
      <span className={cn('text-[11px] font-medium', done ? 'text-emerald-500' : 'text-foreground')}>{label}</span>
      <span className="text-[10px] text-muted-foreground">{done ? '✓' : '+ log'}</span>
    </button>
  )
}

// ─── 4. Couples panel ───────────────────────────────────────────────
function CouplesPanel({ t, userId, partnerId, partnerName, locale }) {
  const navigate = useNavigate()
  const questionDay = useMemo(() => questionsLocalDateKey(), [])
  const wordleDay = useMemo(() => wordleLocalDateKey(), [])

  const myAnswer = useMyAnswer(userId, questionDay)
  const partnerAnswer = usePartnerAnswer(partnerId, questionDay)
  // usePartnerWordleAttempt is the generic "wordle row for this user
  // on this day" hook — reused for our own row here so we can compose
  // both sides' state for a smart status line.
  const myWordle = usePartnerWordleAttempt(userId, wordleDay)
  const partnerWordle = usePartnerWordleAttempt(partnerId, wordleDay)
  const quiz = useQuizRounds(userId, partnerId)

  // Daily question 4-way state machine. Names match the i18n keys so
  // each state line reads contextually instead of one static message.
  const iAnswered = !!myAnswer.row?.choice_id
  const partnerAnswered = !!partnerAnswer.row?.choice_id
  const qState = iAnswered && partnerAnswered ? 'bothDone'
    : iAnswered && !partnerAnswered ? 'iAnsweredWaiting'
    : !iAnswered && partnerAnswered ? 'partnerAnsweredYourTurn'
    : 'neither'

  // Wordle: status is one of 'not_started' | 'playing' | 'won' | 'lost'
  // for each side. We collapse all combinations into a small set of
  // sentences instead of just talking about the partner's state.
  const wordleState = useMemo(() => {
    const mine = myWordle.status
    const theirs = partnerWordle.status
    // I'm done (won or lost), partner state varies
    if (mine === 'won' || mine === 'lost') {
      if (theirs === 'won' || theirs === 'lost') return 'bothFinished'
      if (theirs === 'playing') return 'iDonePartnerPlaying'
      return 'iDoneWaiting'
    }
    // I'm mid-game
    if (mine === 'playing') {
      if (theirs === 'won' || theirs === 'lost') return 'iPlayingPartnerDone'
      if (theirs === 'playing') return 'bothPlaying'
      return 'iPlaying'
    }
    // I haven't started
    if (theirs === 'won' || theirs === 'lost') return 'partnerDoneYourTurn'
    if (theirs === 'playing') return 'partnerPlaying'
    return 'neither'
  }, [myWordle.status, partnerWordle.status])

  // Quiz: derived from the unified useQuizRounds set rather than
  // hand-fetching per-side. Five buckets cover the realistic cases.
  const quizState = useMemo(() => {
    if (!quiz.rounds.length) return 'fresh'
    const enriched = quiz.rounds.map((r) => {
      const ans = quiz.answersByRound.get(r.id) || []
      const myAns = ans.filter((a) => a.user_id === userId).length
      const theirAns = ans.filter((a) => a.user_id === partnerId).length
      return {
        closed: !!r.completed_at,
        myDone: myAns >= ROUND_SIZE,
        partnerDone: theirAns >= ROUND_SIZE,
      }
    })
    const revealReady = enriched.filter((r) => r.closed && r.myDone && r.partnerDone).length
    const myTurn     = enriched.filter((r) => !r.closed && !r.myDone).length
    const waiting    = enriched.filter((r) => !r.closed && r.myDone && !r.partnerDone).length
    if (revealReady > 0) return 'revealReady'
    if (myTurn > 0)      return 'myTurn'
    if (waiting > 0)     return 'waiting'
    return 'fresh'
  }, [quiz.rounds, quiz.answersByRound, userId, partnerId])

  if (!partnerId || !partnerName) return null

  return (
    <Card>
      <CardContent className="p-3 sm:p-4 space-y-3">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-app-primary-500" dir="auto">
          {t('phoenixTracker.today.couplesTitle', { name: partnerName })}
        </p>
        <CouplesRow
          icon={MessagesSquare}
          label={t('phoenixTracker.today.dailyQuestion')}
          state={t(`phoenixTracker.today.qState.${qState}`, { name: partnerName })}
          myUserId={userId}
          partnerId={partnerId}
          onClick={() => navigate('/games/questions')}
        />
        <CouplesRow
          icon={Gamepad2}
          label={t('phoenixTracker.today.wordle')}
          state={t(`phoenixTracker.today.wordleState.${wordleState}`, { name: partnerName })}
          myUserId={userId}
          partnerId={partnerId}
          onClick={() => navigate('/games/wordle')}
        />
        <CouplesRow
          icon={Brain}
          label={t('phoenixTracker.today.quiz')}
          state={t(`phoenixTracker.today.quizState.${quizState}`, { name: partnerName })}
          myUserId={userId}
          partnerId={partnerId}
          onClick={() => navigate('/games/quiz')}
        />
        <MomentsCouplesRow
          t={t}
          locale={locale}
          userId={userId}
          partnerId={partnerId}
          partnerName={partnerName}
          navigate={navigate}
        />
      </CardContent>
    </Card>
  )
}

// Moments row in the couples panel — self-contained because the
// state needs a live subscription to today's prompt + captures.
// Tap routes to the camera when the user still needs to shoot,
// otherwise lands on the module home.
function MomentsCouplesRow({ t, locale, userId, partnerId, partnerName, navigate }) {
  const { prompt, mine, partner, status, windowOpen } = useMomentsToday(null, { locale })
  const todayKsa = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Riyadh' })
  const isToday = !!prompt && prompt.prompt_date === todayKsa

  let momentsState
  if (!isToday) momentsState = 'noPromptYet'
  else if (status === 'revealed') momentsState = 'bothRevealed'
  else if (windowOpen) {
    if (mine && partner) momentsState = 'bothRevealed'
    else if (mine) momentsState = 'iSubmittedWaiting'
    else if (partner) momentsState = 'partnerSubmittedYourTurn'
    else momentsState = 'openNeither'
  } else {
    if (mine && !partner) momentsState = 'partnerMissedMine'
    else if (!mine && partner) momentsState = 'iMissedPartner'
    else momentsState = 'bothMissed'
  }

  const goCapture = isToday && windowOpen && !mine && prompt
    ? `/moments/capture/${prompt.id}`
    : '/moments'

  return (
    <CouplesRow
      icon={Camera}
      label={t('phoenixTracker.today.moments')}
      state={t(`phoenixTracker.today.momentsState.${momentsState}`, { name: partnerName })}
      myUserId={userId}
      partnerId={partnerId}
      onClick={() => navigate(goCapture)}
    />
  )
}

function CouplesRow({ icon: Icon, label, state, myUserId, partnerId, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 text-start py-2"
      dir="auto"
    >
      <span className="p-1.5 rounded-md bg-app-primary-500/10 text-app-primary-500 flex-shrink-0">
        <Icon className="w-4 h-4" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground truncate">{label}</p>
        <p className="text-[11px] text-muted-foreground truncate" dir="auto">{state}</p>
      </div>
      <AvatarPair myUserId={myUserId} partnerId={partnerId} />
      <ChevronRight className="w-4 h-4 text-muted-foreground rtl:-scale-x-100 flex-shrink-0" />
    </button>
  )
}

// Two-avatar stack used in the couples rows so each line shows both
// partners, not just the other one. Mine sits on top of partner's
// with a slight overlap — same visual idiom every couples-style app
// uses for "this is about both of us".
function AvatarPair({ myUserId, partnerId }) {
  return (
    <span className="inline-flex flex-shrink-0" dir="ltr">
      <Avatar userId={partnerId} size={20} className="border-2 border-card" />
      <span className="-ms-2 inline-flex">
        <Avatar userId={myUserId} size={20} className="border-2 border-card" />
      </span>
    </span>
  )
}

// ─── 5. Quick stats ─────────────────────────────────────────────────
function QuickStats({ t }) {
  const { entries } = useWeightEntries()
  const { formatWeight } = useUnits()
  const latest = entries[0] || null
  const prior = entries[1] || null
  const delta = latest && prior ? Number(latest.weight) - Number(prior.weight) : null
  const DeltaIcon = delta == null ? Minus : (delta < 0 ? TrendingDown : delta > 0 ? TrendingUp : Minus)
  const deltaTone = delta == null ? 'bg-muted text-muted-foreground'
    : delta < 0 ? 'bg-emerald-500/15 text-emerald-500'
    : delta > 0 ? 'bg-amber-500/15 text-amber-500'
    : 'bg-muted text-muted-foreground'

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground" dir="auto">
            {t('phoenixTracker.today.currentWeight')}
          </span>
          <span className="p-1.5 rounded-md bg-app-primary-500/10 text-app-primary-500">
            <Scale className="w-3.5 h-3.5" />
          </span>
        </div>
        <div className="flex items-baseline gap-2" dir="ltr">
          <span className="text-3xl font-bold text-foreground font-mono tabular-nums">
            {latest ? formatWeight(Number(latest.weight)) : '—'}
          </span>
          {delta != null && (
            <span className={cn('inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[11px] font-mono tabular-nums font-semibold', deltaTone)}>
              <DeltaIcon className="w-3 h-3" />
              {Math.abs(delta).toFixed(1)}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

// ─── 2b. Habits ─────────────────────────────────────────────────────
// Compact today's-habits check-off. Hidden until the user creates a
// habit (the empty state lives on the full /habits page).
const HABIT_COLOR_CLASS = {
  emerald: 'bg-emerald-500', sky: 'bg-sky-500', violet: 'bg-violet-500',
  amber: 'bg-amber-500', rose: 'bg-rose-500', teal: 'bg-teal-500',
}
function HabitsWidget({ t, navigate }) {
  const { habits, loading, isDone, toggle, todayProgress } = useHabits()
  if (loading || habits.length === 0) return null
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4">
        <button type="button" onClick={() => navigate('/habits')} className="w-full flex items-center justify-between gap-2 mb-2.5">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground" dir="auto">
            {t('habits.title')}
          </span>
          <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground" dir="auto">
            {t('habits.doneOfTotal', { done: todayProgress.done, total: todayProgress.total })}
            <ChevronRight className="w-3.5 h-3.5 rtl:-scale-x-100" />
          </span>
        </button>
        <div className="flex flex-wrap gap-2">
          {habits.map((h) => {
            const done = isDone(h.id)
            return (
              <button
                key={h.id}
                type="button"
                onClick={() => toggle(h.id)}
                className={cn(
                  'inline-flex items-center gap-1.5 ps-2 pe-2.5 h-9 rounded-full text-sm transition-colors',
                  done
                    ? `${HABIT_COLOR_CLASS[h.color] || 'bg-app-primary-500'} text-white`
                    : 'bg-muted text-muted-foreground',
                )}
                dir="auto"
              >
                <span>{h.emoji || (done ? '✓' : '○')}</span>
                <span className="truncate max-w-[90px]">{h.name}</span>
                {done && <Check className="w-3.5 h-3.5" />}
              </button>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}

// ─── 5b. Spy scoreboard teaser ──────────────────────────────────────
// Compact "best bluffer" card; only shows once at least one Spy round
// has been logged. Taps through to the full scoreboard.
function SpyScoreboardWidget({ t, navigate }) {
  const { scoreboard, loading } = useSpyScoreboard()
  if (loading) return null
  const bestBluffer = scoreboard.find((r) => r.spyWins > 0)
  if (!bestBluffer) return null
  return (
    <button
      type="button"
      onClick={() => navigate('/games/spy/scoreboard')}
      className="w-full text-start"
    >
      <Card className="overflow-hidden hover:bg-accent transition-colors">
        <CardContent className="p-4 flex items-center gap-3">
          <span className="p-2 rounded-lg bg-app-primary-500/10 text-app-primary-500 flex-shrink-0">
            <VenetianMask className="w-5 h-5" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground" dir="auto">
              {t('games.spy.bestBluffer')}
            </p>
            <p className="text-sm font-bold text-foreground truncate" dir="auto">
              {bestBluffer.name} · {t('games.spy.spyWinsN', { n: bestBluffer.spyWins })}
            </p>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0 rtl:-scale-x-100" />
        </CardContent>
      </Card>
    </button>
  )
}

// ─── 6. Top headline ────────────────────────────────────────────────
function TopHeadline({ t, locale }) {
  const navigate = useNavigate()
  const { articles, loading } = useNewsArticles({ kindGroup: 'news' })
  const top = articles[0]
  const translated = useTitleTranslation(top, locale)

  if (loading || !top) return null
  const cached = top.translations?.[locale]?.title
  const title = cached || translated || top.title || ''

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] uppercase tracking-wider text-app-primary-500/90" dir="auto">
            {t('phoenixTracker.today.headline')}
          </span>
          {top.source?.name && (
            <span className="text-[10px] text-muted-foreground truncate" dir="auto">
              {top.source.name}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => navigate(`/article/${top.id}?from=news`)}
          className="w-full flex items-start gap-3 text-start"
          dir="auto"
        >
          {top.image_url && (
            <img
              src={top.image_url}
              alt=""
              className="w-16 h-16 rounded-md object-cover flex-shrink-0"
              loading="lazy"
            />
          )}
          <p className="flex-1 min-w-0 text-sm font-medium text-foreground leading-snug">{title}</p>
          <ChevronRight className="w-4 h-4 text-muted-foreground rtl:-scale-x-100 flex-shrink-0 mt-1" />
        </button>
      </CardContent>
    </Card>
  )
}

// On-demand title-only translation for the home headline. If the
// article's language already matches the UI locale, returns null
// (the caller falls back to article.title). Otherwise:
//   1. Returns the cached translation from localStorage immediately
//      so a previously-seen headline renders without a flash.
//   2. Fires translate-text in the background and caches the result
//      under `phoenix:title-translation:<articleId>:<locale>`.
//
// Stateless server-side (we don't write back to news_articles) so
// repeated dashboard mounts re-hit MS Translator if the local cache
// gets cleared — but each translation is ~50-100 chars, well within
// the free F0 monthly budget.
function useTitleTranslation(article, locale) {
  const id = article?.id || null
  const sourceLang = article?.language || null
  const original = article?.title || ''
  const needTranslation = !!(id && sourceLang && locale && sourceLang !== locale)
  const cacheKey = needTranslation ? `phoenix:title-translation:${id}:${locale}` : null

  const [value, setValue] = React.useState(() => {
    if (!cacheKey) return null
    try { return window.localStorage.getItem(cacheKey) || null } catch { return null }
  })

  React.useEffect(() => {
    if (!needTranslation || !original || value) return
    let alive = true
    ;(async () => {
      try {
        const resp = await supabase.functions.invoke('translate-text', {
          body: { strings: [original], target_lang: locale },
        })
        if (!alive) return
        const translated = resp.data?.translations?.[0]
        if (translated && translated !== original) {
          try { window.localStorage.setItem(cacheKey, translated) } catch { /* private mode */ }
          setValue(translated)
        }
      } catch { /* offline / quota — leave value null, caller falls back */ }
    })()
    return () => { alive = false }
  }, [needTranslation, original, locale, cacheKey, value])

  return value
}

// ─── 7. Module grid ─────────────────────────────────────────────────
function ModuleGrid({ t, user, navigate }) {
  // Pull the long-tail modules together so the user has a visual map
  // of "everything else this app does" without having to dig into the
  // profile sheet. Tiles are 3 across — fits comfortably on mobile,
  // doesn't compete with the primary bottom-bar tabs.
  //
  // Owner-only modules sit behind the same gate the routes use so
  // non-owners don't see broken tiles.
  const ownerAccess = hasOwnerAccess(user)
  const ownerOnly = isOwner(user)
  const tiles = [
    { key: 'reports',    icon: FileText,    to: '/reports',     label: t('reports.title') },
    { key: 'finance',    icon: Wallet,      to: '/finance',     label: t('finance.title') },
    { key: 'phases',     icon: Layers,      to: '/phases',      label: t('phoenixTracker.phases.title') },
    { key: 'health',     icon: Stethoscope, to: '/health',      label: t('healthProfile.title') },
    ownerAccess && { key: 'learning',  icon: BookOpen,    to: '/learning',    label: t('learning.title') },
    ownerAccess && { key: 'reading',   icon: Library,     to: '/reading',     label: t('reading.title') },
    ownerOnly && { key: 'journey',   icon: Compass,     to: '/journey',     label: t('journey.title') },
    ownerOnly && { key: 'immigration', icon: Plane,    to: '/immigration', label: t('immigration.title') },
    ownerAccess && { key: 'english',   icon: Languages,   to: '/english',     label: t('english.title') },
  ].filter(Boolean)

  return (
    <Card>
      <CardContent className="p-3 sm:p-4">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-app-primary-500 mb-2" dir="auto">
          {t('phoenixTracker.today.more')}
        </p>
        <div className="grid grid-cols-3 gap-2">
          {tiles.map((tile) => (
            <button
              key={tile.key}
              type="button"
              onClick={() => navigate(tile.to)}
              className="flex flex-col items-center gap-1 px-2 py-3 rounded-lg border border-border bg-card hover:bg-accent transition-colors"
              dir="auto"
            >
              <tile.icon className="w-4 h-4 text-app-primary-500" />
              <span className="text-[11px] font-medium text-foreground text-center truncate w-full">{tile.label}</span>
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
